import { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import produce from 'immer';
import pick from 'lodash/pick';

import { PhotoForCommentsAndReactions } from '../../../graphql/queries/photos';
import { createLoadMoreMethod } from '../../../util/apollo';
import {
  AccountFileCommentCreate,
  AccountFileCommentUpdate,
  AccountFileCommentDelete,
} from '../../../graphql/mutations/comments';
import uuidv4 from '../../../util/uuid';
import { MeQuery } from '../../../graphql/queries/account';
import { VideoForCommentsAndReactions } from '../../../graphql/queries/video';
import useAuthenticated from '../../../hooks/useAuthenticated';

const useFileComments = ({
  limit = 100,
  photoId,
  videoId,
  parentId,
  isProEntity,
}) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const {
    data: { photo } = {},
    loading: loadingPhoto,
    error: photoError,
    fetchMore: photoFetchMore,
  } = useQuery(PhotoForCommentsAndReactions, {
    variables: {
      id: photoId,
      comments_limit: limit,
      comments_offset: 0,
      root_only: !parentId,
      parent_id: parentId,
    },
    skip: !photoId || !authenticated || isProEntity,
  });

  const {
    data: { video } = {},
    loading: loadingVideo,
    error: videoError,
    fetchMore: videoFetchMore,
  } = useQuery(VideoForCommentsAndReactions, {
    variables: {
      id: videoId,
      comments_limit: limit,
      comments_offset: 0,
      root_only: !parentId,
      parent_id: parentId,
    },
    skip: !videoId || !authenticated || isProEntity,
  });

  const [accountFileCommentCreate] = useMutation(AccountFileCommentCreate, {
    update: (
      store,
      {
        data: {
          accountFileCommentCreate: { account_file_comment: comment },
        },
      },
    ) => {
      if (photoId) {
        // ---- UPDATE CURRENT ALBUM DATA:
        const data = store.readQuery({
          query: PhotoForCommentsAndReactions,
          variables: {
            id: photoId,
            comments_limit: limit,
            comments_offset: 0,
            root_only: !parentId,
            parent_id: parentId,
          },
        });

        const updatedData = produce(data, draft => {
          draft.photo.comments = {
            ...draft.photo.comments,
            total: draft.photo.comments.total + 1,
            records: [...draft.photo.comments.records, comment],
          };
        });

        store.writeQuery({
          query: PhotoForCommentsAndReactions,
          variables: {
            id: photoId,
            comments_limit: limit,
            comments_offset: 0,
            root_only: !parentId,
            parent_id: parentId,
          },
          data: updatedData,
        });
      }
      if (videoId) {
        // ---- UPDATE CURRENT ALBUM DATA:
        const data = store.readQuery({
          query: VideoForCommentsAndReactions,
          variables: {
            id: videoId,
            comments_limit: limit,
            comments_offset: 0,
            root_only: !parentId,
            parent_id: parentId,
          },
        });

        const updatedData = produce(data, draft => {
          draft.video.comments = {
            ...draft.video.comments,
            total: draft.video.comments.total + 1,
            records: [...draft.video.comments.records, comment],
          };
        });

        store.writeQuery({
          query: VideoForCommentsAndReactions,
          variables: {
            id: videoId,
            comments_limit: limit,
            comments_offset: 0,
            root_only: !parentId,
            parent_id: parentId,
          },
          data: updatedData,
        });
      }
    },
  });

  const [accountFileCommentDelete] = useMutation(AccountFileCommentDelete);
  const [accountFileCommentUpdate] = useMutation(AccountFileCommentUpdate);

  const entity = photo || video;

  const comments = entity?.comments.records ?? [];
  const commentsTotal = entity?.comments.total ?? 0;

  const loadMore = createLoadMoreMethod({
    source: entity,
    recordsPath: 'comments',
    offsetVariableLabel: 'comments_offset',
    fetchMore: photoFetchMore || videoFetchMore,
    limit,
  });

  const onCommentCreate = async message => {
    await accountFileCommentCreate({
      variables: {
        input: {
          photo_id: photoId,
          video_id: videoId,
          message,
          parent_id: parentId,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        accountFileCommentCreate: {
          __typename: 'AccountFileCommentCreatePayload',
          account_file_comment: {
            __typename: 'AccountFileComment',
            id: uuidv4(),
            created_at: new Date(),
            account: {
              ...pick(user, ['__typename', 'id', 'name', 'username']),
            },
            photo: photoId ? { __typename: 'Photo', id: photoId } : null,
            video: videoId ? { __typename: 'Video', id: videoId } : null,
            parent: parentId
              ? { __typename: 'AccountFileComment', id: parentId }
              : null,
            children: {
              __typename: 'AccountFileCommentCollection',
              total: 0,
            },
            message,
          },
        },
      },
    });
  };

  const onCommentDelete = async accountFileCommentId => {
    await accountFileCommentDelete({
      variables: {
        input: {
          account_file_comment_id: accountFileCommentId,
        },
      },

      update: store => {
        if (photoId) {
          const data = store.readQuery({
            query: PhotoForCommentsAndReactions,
            variables: {
              id: photoId,
              comments_limit: limit,
              comments_offset: 0,
              root_only: !parentId,
              parent_id: parentId,
            },
          });

          const updatedData = produce(data, draft => {
            draft.photo.comments = {
              ...draft.photo.comments,
              total: draft.photo.comments.total - 1,
              records: draft.photo.comments.records.filter(
                accountFileComment =>
                  accountFileComment.id !== accountFileCommentId,
              ),
            };
          });

          store.writeQuery({
            query: PhotoForCommentsAndReactions,
            variables: {
              id: photoId,
              comments_limit: limit,
              comments_offset: 0,
              root_only: !parentId,
              parent_id: parentId,
            },
            data: updatedData,
          });
        }
        if (videoId) {
          const data = store.readQuery({
            query: VideoForCommentsAndReactions,
            variables: {
              id: videoId,
              comments_limit: limit,
              comments_offset: 0,
              root_only: !parentId,
              parent_id: parentId,
            },
          });

          const updatedData = produce(data, draft => {
            draft.video.comments = {
              ...draft.video.comments,
              total: draft.video.comments.total - 1,
              records: draft.video.comments.records.filter(
                accountFileComment =>
                  accountFileComment.id !== accountFileCommentId,
              ),
            };
          });

          store.writeQuery({
            query: VideoForCommentsAndReactions,
            variables: {
              id: videoId,
              comments_limit: limit,
              comments_offset: 0,
              root_only: !parentId,
              parent_id: parentId,
            },
            data: updatedData,
          });
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        accountFileCommentDelete: {
          __typename: 'AccountFileCommentDeletePayload',
          success: true,
        },
      },
    });
  };

  const onCommentUpdate = async (accountFileCommentId, message) => {
    await accountFileCommentUpdate({
      variables: {
        input: {
          account_file_comment_id: accountFileCommentId,
          message,
        },
      },
    });
  };

  const onLoadMore = async () => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await loadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    loading: loadingPhoto || loadingVideo,
    error: photoError || videoError,
    comments,
    commentsTotal,
    user,
    onCommentCreate,
    onCommentUpdate,
    onCommentDelete,
    isOwner: entity?.is_owner,
    isUploader: entity?.is_uploader,
    entity,
    loadingMore,
    onLoadMore,
  };
};

export default useFileComments;
